import React, { useState, useEffect } from "react";
import LoginForm from "@/components/auth/LoginForm";
import SignupForm from "@/components/auth/SignupForm";
import ResetForm from "@/components/auth/ResetForm";
import { ModernAuthCard } from "@/components/ui/modern-auth-card";
import { EmailVerificationDialog } from "@/components/ui/email-verification-dialog";
import { useAuth } from "@/hooks/auth";
import { useSignup } from "@/hooks/useSignup";
import { useAuthRedirect } from "@/hooks/useAuthRedirect";
import Navbar from "@/components/Navbar";

const AuthPage = () => {
  console.log('AuthPage rendering');
  
  // Custom hooks
  const { 
    formType, 
    email, 
    password, 
    loading, 
    setEmail, 
    setPassword, 
    handleLogin, 
    handleReset, 
    switchFormType,
    handleGoogleLogin
  } = useAuth();
  
  const signup = useSignup();
  
  // Automatic redirect if logged in
  useAuthRedirect();
  
  // Handler for the signup form with state change after success
  const handleSignupSubmit = async (e: React.FormEvent) => {
    console.log("Handling signup in AuthPage");
    e.preventDefault(); // Prevent default form behavior
    
    const success = await signup.handleSignup(null, false); // false = não é Google auth
    if (success && !signup.showEmailVerification) {
      switchFormType("login");
    }
  };

  // Handler for Google signup
  const handleGoogleSignupSubmit = async (e: React.MouseEvent) => {
    e.preventDefault();
    await handleGoogleLogin(e); // Usar o handler do useAuth para Google
  };

  // Set the correct form type based on URL
  useEffect(() => {
    const path = window.location.pathname;
    if (path === "/login") {
      switchFormType("login");
    } else if (path === "/cadastro") {
      switchFormType("signup");
    } else if (path === "/recuperar-senha") {
      switchFormType("reset");
    }
  }, [switchFormType]);

  console.log('AuthPage rendering with formType:', formType, 'loading:', loading);
  
  // Title and subtitle based on form type
  const getTitle = () => {
    switch(formType) {
      case "login": return "Entrar";
      case "signup": return "Cadastrar-se";
      case "reset": return "Recuperar Senha";
    }
  };
  
  const getSubtitle = () => {
    switch(formType) {
      case "login": return "Acesse sua conta para continuar";
      case "signup": return "Crie sua conta para começar";
      case "reset": return "Recupere o acesso à sua conta";
    }
  };

  return (
    <div className="bg-[#0f141c] min-h-screen">
      <Navbar />
      <div className="pt-20 flex justify-center items-center min-h-[calc(100vh-80px)]">
        <ModernAuthCard
          title={getTitle()}
          subtitle={getSubtitle()}
          formType={formType}
          onSwitchForm={switchFormType}
        >
          {formType === "login" && (
            <LoginForm
              email={email}
              password={password}
              loading={loading}
              onEmailChange={setEmail}
              onPasswordChange={setPassword}
              onSubmit={handleLogin}
              onGoogleLogin={handleGoogleLogin}
            />
          )}

          {formType === "signup" && (
            <SignupForm
              nickname={signup.nickname}
              email={signup.email}
              password={signup.password}
              whatsappNumber={signup.whatsappNumber}
              preferredGame={signup.preferredGame}
              role={signup.role}
              loading={signup.loading}
              onNicknameChange={signup.setNickname}
              onEmailChange={signup.setEmail}
              onPasswordChange={signup.setPassword}
              onWhatsappNumberChange={signup.setWhatsappNumber}
              onPreferredGameChange={signup.setPreferredGame}
              onRoleChange={signup.setRole}
              onSubmit={handleSignupSubmit}
              onGoogleLogin={handleGoogleSignupSubmit}
            />
          )}

          {formType === "reset" && (
            <ResetForm
              email={email}
              loading={loading}
              onEmailChange={setEmail}
              onSubmit={handleReset}
            />
          )}
        </ModernAuthCard>
        
        {/* Diálogo de Verificação de Email */}
        <EmailVerificationDialog
          isOpen={signup.showEmailVerification}
          onClose={() => {
            signup.closeEmailVerificationDialog();
            switchFormType("login"); // Redirecionar para login após fechar
          }}
          email={signup.registeredEmail}
          isGoogleSignup={signup.isGoogleSignup}
        />
      </div>
    </div>
  );
};

export default AuthPage;
